<template>
  <div class="min-h-screen bg-gray-50 p-6">
    <div class="max-w-6xl mx-auto">
      <!-- 頁面標題 -->
      <div class="mb-2">
        <h1 class="text-3xl font-bold text-gray-900 mb-2">廣告資源管理</h1>
        <p class="text-gray-600">管理機台群組與問答題庫，用於廣告排程系統</p>
      </div>

      <!-- 資源管理頁籤 -->
      <a-tabs default-active-key="1" type="card" class="bg-white rounded-lg shadow-sm" @change="handleTabChange">
        <!-- 機台群組管理頁籤 -->
        <a-tab-pane key="1" tab="機台群組管理">
          <div class="px-6">
            <div class="mb-4 flex gap-5">
              <a-button type="primary" size="large" @click="showGroupModal('add')">
                <a-icon type="plus" />
                新增機台群組
              </a-button>
              <a-button type="default" size="large" @click="showGroupImportModal">
                <a-icon type="import" />
                批次匯入群組
              </a-button>
              <a-button type="link" size="large" @click="downloadGroupTemplate">
                <a-icon type="download" />
                下載匯入模板
              </a-button>
            </div>

            <a-table
              :columns="groupColumns"
              :data-source="machineGroups"
              :loading="loadingGroups"
              :pagination="groupPagination"
              :row-key="record => record.id"
              @change="handleGroupTableChange"
              bordered
            >
              <template slot="name" slot-scope="text, record">
                <span class="font-medium">{{ record.name }}</span>
              </template>
              <template slot="description" slot-scope="text, record">
                <span class="font-medium">{{ record.description }}</span>
              </template>
              <template slot="machineCount" slot-scope="text, record">
                <a-badge
                  :count="record.stores_data ? record.stores_data.length : 0"
                  :overflow-count="99999"
                  :number-style="{ backgroundColor: '#52c41a' }"
                />
              </template>
              <template slot="stores" slot-scope="text, record">
                <div class="flex flex-wrap gap-2 max-w-md">
                  <template v-if="record.stores_data && record.stores_data.length > 0">
                    <a-tag
                      v-for="store in getVisibleStores(record)"
                      :key="store.device_mac"
                      :color="store.status == 1 ? 'green' : 'red'"
                    >
                      {{ store.device_name }}
                    </a-tag>
                    <template v-if="record.stores_data.length > maxVisibleMachines">
                      <a-tag color="blue" style="cursor: pointer" @click="toggleGroupExpansion(record.id)">
                        <a-icon :type="expandedGroups[record.id] ? 'minus' : 'plus'" />
                        {{ expandedGroups[record.id] ? '收起' : `展開更多 (${record.stores_data.length - maxVisibleMachines})` }}
                      </a-tag>
                    </template>
                  </template>
                  <span v-else class="text-gray-400">無機台</span>
                </div>
              </template>
              <template slot="action" slot-scope="text, record">
                <div class="space-x-2">
                  <a-button type="primary" size="small" @click="editGroup(record)">
                    <a-icon type="edit" /> 編輯
                  </a-button>
                  <a-popconfirm
                    title="確定要刪除此群組嗎？"
                    ok-text="確定"
                    cancel-text="取消"
                    @confirm="deleteGroup(record.id)"
                  >
                    <a-button type="danger" size="small">
                      <a-icon type="delete" /> 刪除
                    </a-button>
                  </a-popconfirm>
                </div>
              </template>
            </a-table>
          </div>
        </a-tab-pane>

        <!-- 問答題庫管理頁籤 -->
        <a-tab-pane key="2" tab="問答題庫管理">
          <div class="p-6">
            <div class="mb-4 flex gap-5">
              <a-button type="primary" size="large" @click="showQuestionModal('add')">
                <a-icon type="plus" />
                新增問答題目
              </a-button>
              <a-button type="default" size="large" @click="showImportModal">
                <a-icon type="import" />
                批次導入題目
              </a-button>
              <a-button type="link" size="large" @click="downloadTemplate">
                <a-icon type="download" />
                下載匯入模板
              </a-button>
            </div>

            <a-table
              :columns="questionColumns"
              :data-source="questionBank"
              :loading="loadingQuestions"
              :pagination="questionPagination"
              :row-key="record => record.id"
              @change="handleQuestionTableChange"
              bordered
            >
              <template slot="questionTitle" slot-scope="text">
                <span class="font-medium">{{ text }}</span>
              </template>
              <template slot="question" slot-scope="text">
                <span>{{ text }}</span>
              </template>
              <template slot="options" slot-scope="text, record">
                <div class="space-y-1 max-w-md">
                  <div
                    v-for="(option, index) in record.options.options"
                    :key="index"
                    :class="['p-1 rounded text-sm', record.correct_answer === option ? 'bg-green-100' : '']"
                  >
                    {{ index + 1 }}. {{ option }}
                    <a-icon v-if="record.correct_answer === option" type="check-circle" class="text-green-600 ml-1" />
                  </div>
                </div>
              </template>
              <template slot="action" slot-scope="text, record">
                <div class="space-x-2">
                  <a-button type="primary" size="small" @click="editQuestion(record)">
                    <a-icon type="edit" /> 編輯
                  </a-button>
                  <a-popconfirm
                    title="確定要刪除此題目嗎？"
                    ok-text="確定"
                    cancel-text="取消"
                    @confirm="deleteQuestion(record.id)"
                  >
                    <a-button type="danger" size="small">
                      <a-icon type="delete" /> 刪除
                    </a-button>
                  </a-popconfirm>
                </div>
              </template>
            </a-table>
          </div>
        </a-tab-pane>
      </a-tabs>
    </div>

    <!-- 機台群組編輯對話框 -->
    <a-modal
      :title="groupModalType === 'add' ? '新增機台群組' : '編輯機台群組'"
      :visible="groupModalVisible"
      :confirm-loading="confirmLoading"
      @ok="handleGroupSubmit"
      @cancel="closeGroupModal"
      width="700px"
      wrap-class-name="custom-modal-position"
      :style="{ marginTop: '10px' }"
    >
      <a-form :form="groupForm" layout="vertical">
        <a-form-item label="群組名稱">
          <a-input v-decorator="['name', { rules: [{ required: true, message: '請輸入群組名稱' }] }]" placeholder="請輸入群組名稱" />
        </a-form-item>

        <a-form-item label="群組描述">
          <a-input v-decorator="['description']" placeholder="請輸入群組描述（選填）" type="textarea" :rows="1" />
        </a-form-item>

        <a-form-item label="選擇機台門市">
          <div class="bg-gray-50 p-3 rounded mb-3">
            <div class="flex items-center justify-between mb-3">
              <div>
                <p class="text-sm text-gray-600 mb-1">已選擇門市</p>
                <p class="text-xs text-gray-500">目前共 {{ selectedDevices.length }} 門市</p>
              </div>
              <a-button
                v-if="selectedDevices.length > selectedDevicePreviewCount"
                type="link"
                size="small"
                class="px-0"
                @click="toggleSelectedDevicesExpanded"
              >
                {{ selectedDevicesExpanded ? '收合已選門市' : `展開全部 (${selectedDevices.length})` }}
              </a-button>
            </div>
            <div
              class="flex flex-wrap gap-2 pr-2"
              :class="selectedDevicesExpanded ? 'max-h-48 overflow-y-auto' : 'max-h-24 overflow-hidden'"
            >
              <a-tag
                v-for="device in visibleSelectedDevices"
                :key="device.device_mac"
                :color="device.status == 1 ? 'green' : 'red'"
                closable
                @close="removeSelectedDevice(device.device_mac)"
              >
                {{ device.device_name }} ({{ device.city }})
              </a-tag>
              <a-tag
                v-if="!selectedDevicesExpanded && hiddenSelectedDeviceCount > 0"
                color="blue"
              >
                還有 {{ hiddenSelectedDeviceCount }} 門市未展開
              </a-tag>
              <div v-if="selectedDevices.length === 0" class="text-gray-400 text-sm">尚未選擇任何門市</div>
            </div>
          </div>

          <a-divider orientation="left">機台列表</a-divider>

          <div class="mb-3">
            <a-input-search
              v-model="deviceSearchInput"
              placeholder="搜尋機台名稱或城市，按 Enter 或點搜尋後查詢"
              style="width: 360px"
              enter-button="搜尋"
              allow-clear
              @search="submitDeviceSearch"
            />
          </div>

          <div class="border rounded bg-white">
            <a-table
              :columns="deviceColumns"
              :data-source="filteredDevices"
              :pagination="false"
              :row-key="record => record.device_mac"
              size="small"
              :loading="loadingDevices"
              :row-selection="{
                selectedRowKeys: selectedDeviceIds,
                onChange: onDeviceSelectionChange,
                getCheckboxProps: getCheckboxProps,
                preserveSelectedRowKeys: true,
                onSelect: onDeviceSelect,
                onSelectAll: onDeviceSelectAll
              }"
            >
              <!--
                TODO: 狀態欄位暫時隐藏
                要讓此欄有意義需要：
                1. heartBeat handler 出現時同步更新 DB 的 status = 1
                2. 定時器機制：超過 N 分鐘沒收到 heartBeat 就把 status 改為 0
                目前 heartBeat handler 只更新 in-memory，不寫 DB。
              -->
              <!-- <template slot="status" slot-scope="text">
                <span>
                  <a-badge :status="text == 1 ? 'success' : 'error'" />
                  <span>{{ text == 1 ? '正常運行' : '維護中' }}</span>
                </span>
              </template> -->
            </a-table>

            <div class="flex justify-end px-4 py-3 border-t bg-white">
              <a-pagination
                :current="devicePagination.current"
                :page-size="devicePagination.pageSize"
                :total="devicePagination.total"
                :show-size-changer="devicePagination.showSizeChanger"
                :page-size-options="devicePagination.pageSizeOptions"
                :show-total="devicePagination.showTotal"
                @change="handleDevicePageChange"
                @showSizeChange="handleDevicePageSizeChange"
              />
            </div>
          </div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 問答題目編輯對話框 -->
    <a-modal
      :title="questionModalType === 'add' ? '新增問答題目' : '編輯問答題目'"
      :visible="questionModalVisible"
      :confirm-loading="confirmLoading"
      @ok="handleQuestionSubmit"
      @cancel="closeQuestionModal"
      width="700px"
    >
      <a-form :form="questionForm" layout="vertical">
        <a-form-item label="題目標題">
          <a-input v-decorator="['title', { rules: [{ required: true, message: '請輸入題目標題' }] }]" placeholder="請輸入題目標題，如：環保知識小測驗" />
        </a-form-item>

        <a-form-item label="問題內容">
          <a-input v-decorator="['question', { rules: [{ required: true, message: '請輸入問題內容' }] }]" placeholder="請輸入問題內容，如：以下哪種做法最環保？" />
        </a-form-item>

        <a-form-item label="選項">
          <div class="space-y-3">
            <div v-for="(option, index) in currentOptions" :key="index" class="flex items-center">
              <div class="flex-grow">
                <div class="flex items-center">
                  <span class="mr-2">{{ index + 1 }}.</span>
                  <a-input v-model="currentOptions[index]" placeholder="請輸入選項內容" />
                </div>
              </div>
              <a-radio :checked="correctAnswer === index" @change="() => (correctAnswer = index)" class="ml-2"></a-radio>
            </div>
          </div>

          <div class="mt-4 flex items-center">
            <span class="text-gray-500 mr-2">正確答案：選項 {{ correctAnswer + 1 }}</span>
          </div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 問答批次導入 modal -->
    <a-modal
      title="批次導入問答題目"
      :visible="importModalVisible"
      :confirm-loading="importLoading"
      @ok="handleImportSubmit"
      @cancel="closeImportModal"
      width="800px"
    >
      <div class="space-y-6">
        <div class="bg-blue-50 p-4 rounded-lg">
          <div class="text-blue-700 font-medium mb-2">操作步驟：</div>
          <ol class="list-decimal ml-4 text-blue-700 space-y-1">
            <li>下載匯入模板</li>
            <li>按照模板格式填寫問答題目</li>
            <li>上傳填寫好的Excel檔案</li>
            <li>檢查預覽無誤後點擊確認</li>
          </ol>
        </div>

        <div>
          <p class="mb-2 font-medium">上傳Excel檔案：</p>
          <a-upload-dragger
            name="file"
            :multiple="false"
            :before-upload="beforeUpload"
            :file-list="fileList"
            :remove="handleRemove"
            accept=".xlsx,.xls"
          >
            <p class="ant-upload-drag-icon"><a-icon type="inbox" /></p>
            <p class="ant-upload-text">點擊或拖曳檔案至此區域上傳</p>
            <p class="ant-upload-hint">支援 .xlsx 或 .xls 格式，檔案大小不超過 2MB</p>
          </a-upload-dragger>
        </div>

        <div v-if="parsedData.length > 0">
          <div class="flex items-center justify-between mb-2">
            <div class="font-medium">預覽資料：共 {{ parsedData.length }} 筆</div>
            <div>
              <a-badge :status="validationErrors.length > 0 ? 'error' : 'success'" />
              <span>{{ validationErrors.length > 0 ? '資料有誤，請修正後重新上傳' : '資料驗證通過' }}</span>
            </div>
          </div>

          <div v-if="validationErrors.length > 0" class="mb-4 bg-red-50 p-4 rounded-lg">
            <div class="text-red-700 font-medium mb-2">發現以下問題：</div>
            <ul class="list-disc ml-4 text-red-700">
              <li v-for="(error, index) in validationErrors" :key="index">{{ error }}</li>
            </ul>
          </div>

          <a-table
            :columns="previewColumns"
            :data-source="parsedData"
            :pagination="{ pageSize: 5 }"
            size="small"
            :scroll="{ x: 800 }"
            :row-key="(record, index) => index"
            bordered
          >
            <template slot="options" slot-scope="text, record">
              <div class="space-y-1">
                <div
                  v-for="(option, index) in record.options.options"
                  :key="index"
                  :class="['text-sm', record.correct_answer === option ? 'font-bold text-green-600' : '']"
                >
                  {{ index + 1 }}. {{ option }}
                  <a-icon v-if="record.correct_answer === option" type="check-circle" class="ml-1" />
                </div>
              </div>
            </template>

            <template slot="validation" slot-scope="text, record">
              <div>
                <a-tag :color="record._valid ? 'green' : 'red'">
                  {{ record._valid ? '有效' : '無效' }}
                </a-tag>
                <div v-if="!record._valid" class="text-xs text-red-600 mt-1">
                  {{ record._errors.join(', ') }}
                </div>
              </div>
            </template>
          </a-table>
        </div>
      </div>
    </a-modal>

    <!-- 機台群組批次導入 modal -->
    <a-modal
      title="批次匯入機台群組"
      :visible="groupImportModalVisible"
      :confirm-loading="groupImportLoading"
      @ok="handleGroupImportSubmit"
      @cancel="closeGroupImportModal"
      width="900px"
    >
      <div class="space-y-6">
        <div class="bg-blue-50 p-4 rounded-lg">
          <div class="text-blue-700 font-medium mb-2">操作步驟：</div>
          <ol class="list-decimal ml-4 text-blue-700 space-y-1">
            <li>點擊「下載匯入模板」獲取Excel範本</li>
            <li>在「機台名稱」欄填入店名關鍵字（如：三義中正），系統自動匹配</li>
            <li>上傳填寫好的Excel檔案</li>
            <li>檢查預覽與驗證結果無誤後點擊確認匯入</li>
          </ol>
        </div>

        <div>
          <p class="mb-2 font-medium">上傳Excel檔案：</p>
          <a-upload-dragger
            name="file"
            :multiple="false"
            :before-upload="beforeGroupUpload"
            :file-list="groupFileList"
            :remove="handleGroupRemove"
            accept=".xlsx,.xls"
          >
            <p class="ant-upload-drag-icon"><a-icon type="inbox" /></p>
            <p class="ant-upload-text">點擊或拖曳檔案至此區域上傳</p>
            <p class="ant-upload-hint">支援 .xlsx 或 .xls 格式，檔案大小不超過 2MB</p>
          </a-upload-dragger>
        </div>

        <div v-if="groupParsedData.length > 0">
          <div class="flex items-center justify-between mb-2">
            <div class="font-medium">預覽資料：共 {{ groupParsedData.length }} 個群組</div>
            <div>
              <a-badge :status="groupValidationErrors.length > 0 ? 'error' : 'success'" />
              <span>{{ groupValidationErrors.length > 0 ? '資料有誤，請修正後重新上傳' : '資料驗證通過' }}</span>
            </div>
          </div>

          <div v-if="groupValidationErrors.length > 0" class="mb-4 bg-red-50 p-4 rounded-lg">
            <div class="text-red-700 font-medium mb-2">發現以下問題：</div>
            <ul class="list-disc ml-4 text-red-700">
              <li v-for="(error, index) in groupValidationErrors" :key="index">{{ error }}</li>
            </ul>
          </div>

          <a-table
            :columns="groupPreviewColumns"
            :data-source="groupParsedData"
            :pagination="{ pageSize: 5 }"
            size="small"
            :scroll="{ x: 1000 }"
            :row-key="(record, index) => index"
            bordered
          >
            <template slot="nameList" slot-scope="text">
              <div class="max-w-xs truncate" :title="text">
                {{ text || '無' }}
              </div>
            </template>

            <template slot="validation" slot-scope="text, record">
              <div>
                <a-tag :color="record._valid ? 'green' : 'red'">
                  {{ record._valid ? '有效' : '無效' }}
                </a-tag>
                <div v-if="!record._valid" class="text-xs text-red-600 mt-1">
                  {{ record._errors.join(', ') }}
                </div>
              </div>
            </template>
          </a-table>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import FileSaver from 'file-saver'
export default {
  name: 'AdResourcesPage',
  data () {
    return {
      // API 路徑配置
      baseURL: this.GLOBAL.baseURL,
      basePort: this.GLOBAL.basePort,

      // 資料表格列定義
      groupColumns: [
        { title: '群組名稱', dataIndex: 'name', key: 'name', scopedSlots: { customRender: 'name' } },
        { title: '群組敘述', dataIndex: 'description', key: 'description', scopedSlots: { customRender: 'description' } },
        { title: '機台數量', dataIndex: 'machineCount', key: 'machineCount', scopedSlots: { customRender: 'machineCount' } },
        { title: '門市列表', dataIndex: 'stores', key: 'stores', scopedSlots: { customRender: 'stores' } },
        { title: '操作', key: 'action', scopedSlots: { customRender: 'action' } }
      ],
      questionColumns: [
        { title: '題目標題', dataIndex: 'title', key: 'title', scopedSlots: { customRender: 'questionTitle' } },
        { title: '問題內容', dataIndex: 'question', key: 'question', scopedSlots: { customRender: 'question' } },
        { title: '選項', dataIndex: 'options', key: 'options', scopedSlots: { customRender: 'options' } },
        { title: '操作', key: 'action', scopedSlots: { customRender: 'action' } }
      ],

      // 設備列表分頁相關
      devicePagination: {
        current: 1,
        pageSize: 8,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        showTotal: (total) => `共 ${total} 台機台`
      },

      // 機台群組分頁相關
      groupPagination: {
        current: 1,
        pageSize: 3,
        showSizeChanger: true,
        pageSizeOptions: ['3', '10', '20', '50'],
        showTotal: (total) => `共 ${total} 筆資料`
      },

      // 問答題庫分頁相關
      questionPagination: {
        current: 1,
        pageSize: 4,
        showSizeChanger: true,
        pageSizeOptions: ['4', '10', '20', '50'],
        showTotal: (total) => `共 ${total} 筆資料`
      },

      // 資料列表
      machineGroups: [],
      questionBank: [],

      // 加載狀態
      loadingGroups: false,
      loadingQuestions: false,

      // 表單相關
      groupForm: this.$form.createForm(this),
      questionForm: this.$form.createForm(this),
      confirmLoading: false,

      // 機台群組模態窗相關
      groupModalVisible: false,
      groupModalType: 'add', // 'add' 或 'edit'
      currentGroupId: null,
      currentStores: [],
      newStore: {
        id: '',
        name: '',
        city: '',
        status: 1
      },

      // 問答題目模態窗相關
      questionModalVisible: false,
      questionModalType: 'add', // 'add' 或 'edit'
      currentQuestionId: null,
      currentOptions: ['', '', ''],
      correctAnswer: 0,

      // 機台列表相關
      deviceList: [],
      filteredDevices: [],
      selectedDevices: [],
      selectedDeviceIds: [],
      deviceSearchInput: '',
      selectedDevicesExpanded: false,
      selectedDevicePreviewCount: 12,
      deviceSearchText: '',
      deviceListRequestToken: 0,
      loadingDevices: false,
      deviceColumns: [
        { title: '機台MAC', dataIndex: 'device_mac', key: 'device_mac', width: '120px' },
        { title: '機台名稱', dataIndex: 'device_name', key: 'device_name' },
        { title: '城市', dataIndex: 'city', key: 'city', width: '100px' }
        // TODO: 狀態欄位暫時隐藏，待 heartBeat 寫 DB + 超時偵測機制完善後再開啟
        // { title: '狀態', dataIndex: 'status', key: 'status', width: '100px', scopedSlots: { customRender: 'status' } }
      ],
      // Excel導入相關
      importModalVisible: false,
      importLoading: false,
      fileList: [],
      parsedData: [],
      validationErrors: [],
      previewColumns: [
        { title: '題目標題', dataIndex: 'title', key: 'title', width: 150 },
        { title: '問題內容', dataIndex: 'question', key: 'question', width: 200 },
        { title: '選項', dataIndex: 'options', key: 'options', scopedSlots: { customRender: 'options' }, width: 250 },
        { title: '驗證結果', dataIndex: 'validation', key: 'validation', scopedSlots: { customRender: 'validation' }, width: 120 }
      ],
      expandedGroups: {},
      maxVisibleMachines: 10, // 最大可見機台數量
      // Excel導入相關 - 機台群組批次匯入
      groupImportModalVisible: false,
      groupImportLoading: false,
      groupFileList: [],
      groupParsedData: [], // 解析後的群組物件陣列 [{ name, description, stores_data: [...] }, ...]
      groupValidationErrors: [],
      groupPreviewColumns: [
        { title: '群組名稱', dataIndex: 'name', key: 'name', width: 150 },
        { title: '群組描述', dataIndex: 'description', key: 'description', width: 200 },
        { title: '機台數量', dataIndex: 'machineCount', key: 'machineCount', width: 100 },
        { title: '機台名稱列表', dataIndex: 'nameList', key: 'nameList', scopedSlots: { customRender: 'nameList' }, width: 350 },
        { title: '驗證結果', dataIndex: 'validation', key: 'validation', scopedSlots: { customRender: 'validation' }, width: 120 }
      ]
    }
  },

  computed: {
    newStoreValid () {
      return this.newStore.id && this.newStore.name && this.newStore.city
    },
    visibleSelectedDevices () {
      if (this.selectedDevicesExpanded) {
        return this.selectedDevices
      }
      return this.selectedDevices.slice(0, this.selectedDevicePreviewCount)
    },
    hiddenSelectedDeviceCount () {
      return Math.max(this.selectedDevices.length - this.selectedDevicePreviewCount, 0)
    },
    TESTdeviceList: {
      get () {
        return this.$store.state.deviceList
      },
      set () {}
    }
  },

  created () {
    this.fetchMachineGroups()
  },

  methods: {
    // 頁籤切換處理
    handleTabChange (activeKey) {
      if (activeKey === '1') {
        this.fetchMachineGroups()
      } else if (activeKey === '2') {
        this.fetchQuestionBank()
      }
    },

    // 處理機台群組分頁變更
    handleGroupTableChange (pagination) {
      this.groupPagination.current = pagination.current
      this.groupPagination.pageSize = pagination.pageSize
    },

    // 處理問答題庫分頁變更
    handleQuestionTableChange (pagination) {
      this.questionPagination.current = pagination.current
      this.questionPagination.pageSize = pagination.pageSize
    },

    // ===== API 呼叫 =====

    // 獲取機台列表
    fetchDeviceList (page = 1, limit = 8, search = '') {
      const requestToken = this.deviceListRequestToken + 1
      this.deviceListRequestToken = requestToken
      this.loadingDevices = true
      const params = {
        page: page,
        limit: limit,
        search: search
      }
      this.axios
        .post(`${this.baseURL}${this.basePort}/device/deviceList`, params)
        .then((res) => {
          if (requestToken !== this.deviceListRequestToken) {
            return
          }

          if (res.data.message === 'success') {
            this.deviceList = Object.freeze(
              (res.data.data || []).map((device) => ({
                device_mac: device.device_mac,
                device_name: device.device_name,
                city: device.city || device.location || '未知',
                status: device.status ?? 1
              }))
            )
            this.filteredDevices = [...this.deviceList]
            // 更新分頁資訊
            if (res.data.pagination) {
              this.devicePagination.total = res.data.pagination.totalItems
              this.devicePagination.current = res.data.pagination.page
              this.devicePagination.pageSize = res.data.pagination.limit
            } else {
              this.devicePagination.current = page
              this.devicePagination.pageSize = limit
              this.devicePagination.total = this.deviceList.length
            }
          } else {
            this.$message.error('獲取機台列表失敗：' + res.data.message)
          }
        })
        .catch((error) => {
          if (requestToken !== this.deviceListRequestToken) {
            return
          }

          console.error('獲取機台列表出錯:', error)
          this.$message.error('獲取機台列表失敗，請稍後重試')
        })
        .finally(() => {
          if (requestToken === this.deviceListRequestToken) {
            this.loadingDevices = false
          }
        })
    },

    handleDevicePageChange (page, pageSize) {
      this.fetchDeviceList(page, pageSize, this.deviceSearchText)
    },

    handleDevicePageSizeChange (current, size) {
      this.fetchDeviceList(current, size, this.deviceSearchText)
    },

    submitDeviceSearch (searchText) {
      this.deviceSearchText = (typeof searchText === 'string' ? searchText : this.deviceSearchInput).trim()
      this.devicePagination.current = 1 // 搜尋時重設到第一頁
      this.fetchDeviceList(1, this.devicePagination.pageSize, this.deviceSearchText)
    },

    // 處理單個機台選擇
    onDeviceSelect (record, selected) {
      if (selected) {
        // 選中：添加到已選列表
        const exists = this.selectedDevices.find((device) => device.device_mac === record.device_mac)
        if (!exists) {
          this.selectedDevices.push({
            device_mac: record.device_mac,
            device_name: record.device_name,
            city: record.city,
            status: record.status
          })
        }
        // 添加到 ID 列表
        if (!this.selectedDeviceIds.includes(record.device_mac)) {
          this.selectedDeviceIds.push(record.device_mac)
        }
      } else {
        // 取消選中：從已選列表移除
        this.selectedDevices = this.selectedDevices.filter((device) => device.device_mac !== record.device_mac)
        // 從 ID 列表移除
        this.selectedDeviceIds = this.selectedDeviceIds.filter((id) => id !== record.device_mac)
      }
    },

    // 處理當前頁全選/取消全選
    onDeviceSelectAll (selected, selectedRows, changeRows) {
      if (selected) {
        // 全選：添加當前頁所有機台
        changeRows.forEach((row) => {
          const exists = this.selectedDevices.find((device) => device.device_mac === row.device_mac)
          if (!exists) {
            this.selectedDevices.push({
              device_mac: row.device_mac,
              device_name: row.device_name,
              city: row.city,
              status: row.status
            })
          }
          if (!this.selectedDeviceIds.includes(row.device_mac)) {
            this.selectedDeviceIds.push(row.device_mac)
          }
        })
      } else {
        // 取消全選：移除當前頁所有機台
        const currentPageMacs = changeRows.map((row) => row.device_mac)
        this.selectedDevices = this.selectedDevices.filter((device) => !currentPageMacs.includes(device.device_mac))
        this.selectedDeviceIds = this.selectedDeviceIds.filter((id) => !currentPageMacs.includes(id))
      }
    },

    // 處理機台選擇變更
    onDeviceSelectionChange (selectedRowKeys, selectedRows) {
      this.selectedDeviceIds = selectedRowKeys // 更新選中的 ID 列表

      // 獲取當前頁面被取消選擇的機台 MAC
      const currentPageDeviceMacs = this.filteredDevices.map((device) => device.device_mac)
      const currentPageSelectedMacs = selectedRows.map((row) => row.device_mac)
      const currentPageUnselectedMacs = currentPageDeviceMacs.filter((mac) => !currentPageSelectedMacs.includes(mac))

      // 移除當前頁面被取消選擇的機台
      this.selectedDevices = this.selectedDevices.filter((device) => !currentPageUnselectedMacs.includes(device.device_mac))

      // 添加當前頁面新選擇的機台（避免重複）
      selectedRows.forEach((row) => {
        const exists = this.selectedDevices.find((device) => device.device_mac === row.device_mac)
        if (!exists) {
          this.selectedDevices.push({
            device_mac: row.device_mac,
            device_name: row.device_name,
            city: row.city,
            status: row.status
          })
        }
      })
    },

    // 移除已選擇的機台
    removeSelectedDevice (deviceMac) {
      // 從已選設備列表移除
      this.selectedDevices = this.selectedDevices.filter((device) => device.device_mac !== deviceMac)
      // 從已選 ID 列表移除
      this.selectedDeviceIds = this.selectedDeviceIds.filter((id) => id !== deviceMac)

      console.log('移除後 selectedDevices:', this.selectedDevices)
      console.log('移除後 selectedDeviceIds:', this.selectedDeviceIds)
    },

    toggleSelectedDevicesExpanded () {
      this.selectedDevicesExpanded = !this.selectedDevicesExpanded
    },

    // 獲取複選框屬性（可用於控制哪些機台可選）
    getCheckboxProps (record) {
      return {
        // 例如：可以設置維護中的機台不可選
        // disabled: record.status === 'maintenance'
      }
    },

    // ===== 機台群組管理 =====// 更新 showGroupModal 方法
    showGroupModal (type, record = null) {
      this.groupModalType = type
      this.groupModalVisible = true
      this.deviceSearchInput = ''
      this.deviceSearchText = ''

      // 重置分頁到第一頁
      this.devicePagination.current = 1

      // 加載機台列表第一頁數據
      this.fetchDeviceList(1, this.devicePagination.pageSize)

      if (type === 'edit' && record) {
        // 編輯模式處理邏輯...
        this.currentGroupId = record.id
        this.selectedDevices = [...(record.stores_data || [])]
        this.selectedDeviceIds = (record.stores_data || []).map((store) => store.device_mac)
        this.selectedDevicesExpanded = false

        // 使用 $nextTick 確保表單已渲染後再設定值
        this.$nextTick(() => {
          this.groupForm.setFieldsValue({
            name: record.name || '',
            description: record.description || ''
          })
        })
      } else {
        // 新增模式處理邏輯...
        this.currentGroupId = null
        this.selectedDevices = [] // 清空已選設備
        this.selectedDeviceIds = [] // 清空已選 ID
        this.selectedDevicesExpanded = false

        // 重置表單欄位
        this.$nextTick(() => {
          this.groupForm.resetFields()
        })
      }
    },
    // 獲取機台群組列表
    fetchMachineGroups () {
      this.loadingGroups = true
      this.axios
        .get(`${this.baseURL}${this.basePort}/ads/groups`)
        .then((res) => {
          if (res.data.message === 'success') {
            this.machineGroups = res.data.data
          } else {
            this.$message.error('獲取機台群組失敗：' + res.data.message)
          }
        })
        .catch((error) => {
          console.error('獲取機台群組出錯:', error)
          this.$message.error('獲取機台群組失敗，請稍後重試')
        })
        .finally(() => {
          this.loadingGroups = false
        })
    },
    // 獲取問答題庫列表
    fetchQuestionBank () {
      this.loadingQuestions = true
      this.axios
        .get(`${this.baseURL}${this.basePort}/questionBank/list`)
        .then((res) => {
          if (res.data.message === 'success') {
            this.questionBank = res.data.data
          } else {
            this.$message.error('獲取問答題庫失敗：' + res.data.message)
          }
        })
        .catch((error) => {
          console.error('獲取問答題庫出錯:', error)
          this.$message.error('獲取問答題庫失敗，請稍後重試')
        })
        .finally(() => {
          this.loadingQuestions = false
        })
    },

    closeGroupModal () {
      this.groupModalVisible = false
      this.deviceSearchInput = ''
      this.deviceSearchText = ''
      this.selectedDevicesExpanded = false
      this.resetNewStore()
    },

    resetNewStore () {
      this.newStore = {
        id: '',
        name: '',
        city: '',
        status: 1
      }
    },

    addStore () {
      if (this.newStoreValid) {
        this.currentStores.push({ ...this.newStore })
        this.resetNewStore()
      } else {
        this.$message.warning('請填寫完整的門市資訊')
      }
    },

    handleGroupSubmit () {
      this.groupForm.validateFields((err, values) => {
        if (err) {
          return
        }

        if (this.selectedDevices.length === 0) {
          this.$message.warning('請至少選擇一個機台門市')
          return
        }

        // 檢查名稱長度限制
        if (values.name.length > 50) {
          this.$message.warning('群組名稱不可超過50個字元')
          return
        }

        this.confirmLoading = true

        // 準備提交的數據，符合新的API結構
        const submitData = {
          name: values.name,
          description: values.description || '', // 可選描述欄位
          stores_data: this.selectedDevices
        }

        if (this.groupModalType === 'add') {
          // 新增模式，使用新的API路徑
          this.axios
            .post(`${this.baseURL}${this.basePort}/ads/group/create`, submitData)
            .then((res) => {
              if (res.data.message === 'success') {
                this.$message.success('新增群組成功')
                this.fetchMachineGroups() // 重新獲取列表
                this.groupModalVisible = false
              } else {
                this.$message.error(res.data.errorMsg || '新增群組失敗')
              }
            })
            .catch((error) => {
              console.error('新增群組出錯:', error)

              // 處理特定錯誤情況
              if (error.response) {
                if (error.response.status === 409) {
                  this.$message.error('群組名稱已存在，請使用其他名稱')
                } else if (error.response.status === 400) {
                  this.$message.error(error.response.data.errorMsg || '提交的數據無效')
                } else {
                  this.$message.error(error.response.data.errorMsg || '新增群組失敗，請稍後重試')
                }
              } else {
                this.$message.error('新增群組失敗，請檢查網絡連接')
              }
            })
            .finally(() => {
              this.confirmLoading = false
            })
        } else {
          // 編輯模式 - 使用新的API路徑
          this.axios
            .put(`${this.baseURL}${this.basePort}/ads/group/update/${this.currentGroupId}`, submitData)
            .then((res) => {
              if (res.data.message === 'success') {
                this.$message.success('更新群組成功')
                this.fetchMachineGroups() // 重新獲取列表
                this.groupModalVisible = false
              } else {
                this.$message.error(res.data.errorMsg || '更新群組失敗')
              }
            })
            .catch((error) => {
              console.error('更新群組出錯:', error)

              // 處理特定錯誤情況
              if (error.response) {
                if (error.response.status === 409) {
                  this.$message.error('群組名稱已存在，請使用其他名稱')
                } else if (error.response.status === 404) {
                  this.$message.error('找不到指定的群組')
                } else if (error.response.status === 400) {
                  this.$message.error(error.response.data.errorMsg || '提交的數據無效')
                } else {
                  this.$message.error(error.response.data.errorMsg || '更新群組失敗')
                }
              } else {
                this.$message.error('更新群組失敗，請檢查網絡連接')
              }
            })
            .finally(() => {
              this.confirmLoading = false
            })
        }
      })
    },

    editGroup (record) {
      this.showGroupModal('edit', record)
    },

    deleteGroup (id) {
      this.loadingGroups = true
      this.axios
        .delete(`${this.baseURL}${this.basePort}/ads/group/delete/${id}`)
        .then((res) => {
          if (res.data.message === 'success') {
            this.$message.success('刪除群組成功')
            this.fetchMachineGroups() // 重新獲取列表
          } else {
            this.$message.error(res.data.errorMsg || '刪除群組失敗')
          }
        })
        .catch((error) => {
          console.error('刪除群組出錯:', error)
          // 處理特定錯誤狀態
          if (error.response) {
            if (error.response.status === 409) {
              this.$message.error(error.response.data.errorMsg || '無法刪除此群組，因為它正在被廣告活動使用')
            } else if (error.response.status === 404) {
              this.$message.error('找不到指定的群組')
            } else if (error.response.status === 400) {
              this.$message.error('無效的群組ID')
            } else {
              this.$message.error(error.response.data.errorMsg || '刪除群組失敗，請稍後重試')
            }
          } else {
            this.$message.error('刪除群組失敗，請檢查網絡連接')
          }
        })
        .finally(() => {
          this.loadingGroups = false
        })
    },

    // ===== 問答題庫管理 =====
    showQuestionModal (type, record = null) {
      console.log('showQuestionModal', type, record)
      this.questionModalType = type
      this.questionModalVisible = true

      if (type === 'edit' && record) {
        this.currentQuestionId = record.id
        this.currentOptions = [...record.options.options]

        // 找到正確答案在選項陣列中的索引
        this.correctAnswer = record.options.options.findIndex((option) => option === record.correct_answer)
        // 如果沒找到，默認為第一個選項
        if (this.correctAnswer === -1) this.correctAnswer = 0

        // 設置表單初始值
        this.$nextTick(() => {
          this.questionForm.setFieldsValue({
            title: record.title,
            question: record.question
          })
        })
      } else {
        this.currentQuestionId = null
        this.currentOptions = ['', '', '']
        this.correctAnswer = 0

        // 重置表單
        this.$nextTick(() => {
          this.questionForm.resetFields()
        })
      }
    },

    closeQuestionModal () {
      this.questionModalVisible = false
    },

    addOption () {
      if (this.currentOptions.length < 6) {
        this.currentOptions.push('')
      }
    },

    removeOption (index) {
      if (this.currentOptions.length > 2) {
        this.currentOptions.splice(index, 1)

        // 如果刪除的是正確答案或之前的選項，需要調整正確答案的索引
        if (index === this.correctAnswer) {
          // 如果刪除的就是正確答案，將正確答案設為第一個選項
          this.correctAnswer = 0
        } else if (index < this.correctAnswer) {
          // 如果刪除的選項在正確答案之前，正確答案索引減一
          this.correctAnswer--
        }
      }
    },

    handleQuestionSubmit () {
      this.questionForm.validateFields((err, values) => {
        if (err) {
          return
        }

        // 檢查選項是否都填寫了
        const emptyOptions = this.currentOptions.filter((opt) => !opt.trim())
        if (emptyOptions.length > 0) {
          this.$message.warning('請填寫所有選項內容')
          return
        }

        // 檢查標題長度限制
        if (values.title.length > 100) {
          this.$message.warning('標題不可超過100個字元')
          return
        }

        this.confirmLoading = true

        // 準備提交的數據 - 將正確答案從索引改為實際的選項值
        const submitData = {
          title: values.title,
          question: values.question,
          options: {
            options: this.currentOptions
          },
          correct_answer: this.currentOptions[this.correctAnswer] // 使用選項值而非索引
        }

        if (this.questionModalType === 'add') {
          // 新增模式 - 使用新的API路徑
          this.axios
            .post(`${this.baseURL}${this.basePort}/questionBank/create`, submitData)
            .then((res) => {
              if (res.data.message === 'success') {
                this.$message.success('新增問答題目成功')
                this.fetchQuestionBank() // 重新獲取列表
                this.questionModalVisible = false
              } else {
                this.$message.error(res.data.errorMsg || '新增問答題目失敗')
              }
            })
            .catch((error) => {
              console.error('新增問答題目出錯:', error)
              // 處理具體錯誤情況
              if (error.response) {
                this.$message.error(error.response.data.errorMsg || '新增問答題目失敗')
              } else {
                this.$message.error('新增問答題目失敗，請檢查網絡連接')
              }
            })
            .finally(() => {
              this.confirmLoading = false
            })
        } else {
          // 編輯模式 - 使用新的API路徑
          this.axios
            .put(`${this.baseURL}${this.basePort}/questionBank/update/${this.currentQuestionId}`, submitData)
            .then((res) => {
              if (res.data.message === 'success') {
                this.$message.success('更新問答題目成功')
                this.fetchQuestionBank() // 重新獲取列表
                this.questionModalVisible = false
              } else {
                this.$message.error(res.data.errorMsg || '更新問答題目失敗')
              }
            })
            .catch((error) => {
              console.error('更新問答題目出錯:', error)
              // 處理具體錯誤情況
              if (error.response) {
                this.$message.error(error.response.data.errorMsg || '更新問答題目失敗')
              } else {
                this.$message.error('更新問答題目失敗，請檢查網絡連接')
              }
            })
            .finally(() => {
              this.confirmLoading = false
            })
        }
      })
    },

    editQuestion (record) {
      this.showQuestionModal('edit', record)
    },

    deleteQuestion (id) {
      this.loadingQuestions = true
      this.axios
        .delete(`${this.baseURL}${this.basePort}/questionBank/delete/${id}`)
        .then((res) => {
          if (res.data.message === 'success') {
            this.$message.success('刪除問答題目成功')
            this.fetchQuestionBank() // 重新獲取列表
          } else {
            this.$message.error(res.data.errorMsg || '刪除問答題目失敗')
          }
        })
        .catch((error) => {
          console.error('刪除問答題目出錯:', error)
          // 處理特定錯誤狀態
          if (error.response) {
            if (error.response.status === 409) {
              this.$message.error('無法刪除此問題，因為它正在被其他資料引用')
            } else if (error.response.status === 404) {
              this.$message.error('找不到指定的問題')
            } else if (error.response.status === 400) {
              this.$message.error('無效的問題ID')
            } else {
              this.$message.error(error.response.data.errorMsg || '刪除問答題目失敗')
            }
          } else {
            this.$message.error('刪除問答題目失敗，請檢查網絡連接')
          }
        })
        .finally(() => {
          this.loadingQuestions = false
        })
    },
    // 顯示導入模態窗
    showImportModal () {
      this.importModalVisible = true
      this.fileList = []
      this.parsedData = []
      this.validationErrors = []
    },

    // 關閉導入模態窗
    closeImportModal () {
      this.importModalVisible = false
    },

    // 下載模板
    downloadTemplate () {
      // 創建模板數據
      const templateData = [
        {
          題目標題: '環保知識測驗',
          問題內容: '下列哪一項行為最有助於減少碳足跡？',
          選項A: '使用一次性塑膠袋',
          選項B: '選擇公共交通工具代替開車',
          選項C: '每天洗澡超過30分鐘',
          正確答案: '選擇公共交通工具代替開車'
        },
        {
          題目標題: '健康生活小常識',
          問題內容: '成人每日建議飲水量約為多少？',
          選項A: '500ml',
          選項B: '1000ml',
          選項C: '1500-2000ml',
          正確答案: '1500-2000ml'
        }
      ]

      // 創建工作簿和工作表
      const ws = XLSX.utils.json_to_sheet(templateData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '問答題庫模板')

      // 設置列寬
      const colWidths = [
        { wch: 20 }, // 題目標題
        { wch: 30 }, // 問題內容
        { wch: 20 }, // 選項A
        { wch: 20 }, // 選項B
        { wch: 20 }, // 選項C
        { wch: 25 } // 正確答案
      ]
      ws['!cols'] = colWidths

      // 添加說明工作表
      const instructionData = [
        ['問答題庫導入說明'],
        [''],
        ['1. 題目標題：必填，不超過100個字元'],
        ['2. 問題內容：必填，問題的具體描述'],
        ['3. 選項A-C：必須填寫剛好三個選項（A、B、C都要填）'],
        ['4. 正確答案：必填，必須與其中一個選項內容完全相同'],
        ['5. 每行代表一個問答題目'],
        ['6. 可同時導入多個問答題目（建議一次不超過100個）']
      ]
      const wsInstruction = XLSX.utils.aoa_to_sheet(instructionData)
      XLSX.utils.book_append_sheet(wb, wsInstruction, '使用說明')

      // 導出Excel文件
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' })
      FileSaver.saveAs(blob, '問答題庫導入模板.xlsx')
    },

    // 上傳前處理
    beforeUpload (file) {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'application/vnd.ms-excel'

      if (!isExcel) {
        this.$message.error('只能上傳Excel文件！')
        return false
      }

      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isLt2M) {
        this.$message.error('文件大小不能超過2MB！')
        return false
      }

      this.fileList = [file]
      this.parsedData = []
      this.validationErrors = []
      this.parseExcelFile(file)

      return false // 阻止自動上傳
    },

    // 移除文件
    handleRemove () {
      this.fileList = []
      this.parsedData = []
      this.validationErrors = []
      return true
    },

    // 解析Excel文件
    parseExcelFile (file) {
      const reader = new FileReader()

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })

          // 獲取第一個工作表
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]

          // 轉換為JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet)

          if (jsonData.length === 0) {
            this.validationErrors = ['文件中沒有找到任何數據']
            return
          }

          // 解析和驗證數據
          this.processExcelData(jsonData)
        } catch (error) {
          console.error('解析Excel文件出錯:', error)
          this.validationErrors = ['解析Excel文件出錯: ' + error.message]
        }
      }

      reader.onerror = () => {
        this.validationErrors = ['讀取文件時發生錯誤']
      }

      reader.readAsArrayBuffer(file)
    },

    // 處理Excel數據
    processExcelData (jsonData) {
      const parsedData = []
      const globalErrors = []

      if (jsonData.length > 100) {
        globalErrors.push('單次導入不建議超過100條記錄，目前有 ' + jsonData.length + ' 條')
      }

      // 檢查標題行是否包含必要的列
      const requiredColumns = ['題目標題', '問題內容', '選項A', '選項B', '正確答案']
      const firstRow = jsonData[0]

      const missingColumns = requiredColumns.filter((col) => !(col in firstRow))
      if (missingColumns.length > 0) {
        globalErrors.push('缺少必要的列: ' + missingColumns.join(', '))
        this.validationErrors = globalErrors
        return
      }

      // 處理每一行數據
      jsonData.forEach((row, rowIndex) => {
        const rowNum = rowIndex + 2 // Excel是從1開始，加上標題行
        const item = {
          title: row['題目標題']?.trim() || '',
          question: row['問題內容']?.trim() || '',
          options: { options: [] },
          correct_answer: row['正確答案']?.trim() || '',
          _valid: true,
          _errors: []
        }

        // 收集選項
        for (let i = 'A'.charCodeAt(0); i <= 'F'.charCodeAt(0); i++) {
          const optionKey = `選項${String.fromCharCode(i)}`
          if (optionKey in row && row[optionKey]?.trim()) {
            item.options.options.push(row[optionKey].trim())
          }
        }

        // 驗證邏輯
        // 1. 標題必填且長度限制
        if (!item.title) {
          item._errors.push('題目標題為必填項')
          item._valid = false
        } else if (item.title.length > 100) {
          item._errors.push('題目標題不可超過100個字元')
          item._valid = false
        }

        // 2. 問題內容必填
        if (!item.question) {
          item._errors.push('問題內容為必填項')
          item._valid = false
        }

        // 3. 必須剛好三個選項
        if (item.options.options.length !== 3) {
          item._errors.push('必須剛好提供三個選項（選項A、B、C），不可多不可少')
          item._valid = false
        }

        // 4. 正確答案必須是選項之一
        if (!item.correct_answer) {
          item._errors.push('正確答案為必填項')
          item._valid = false
        } else if (!item.options.options.includes(item.correct_answer)) {
          item._errors.push('正確答案必須與選項之一完全相同')
          item._valid = false
        }

        // 如果有錯誤，添加到全局錯誤列表
        if (!item._valid) {
          globalErrors.push(`第 ${rowNum} 行: ${item._errors.join(', ')}`)
        }

        parsedData.push(item)
      })

      this.parsedData = parsedData
      this.validationErrors = globalErrors
    },

    // 提交導入數據
    handleImportSubmit () {
      if (this.parsedData.length === 0) {
        this.$message.warning('請先上傳並解析Excel文件')
        return
      }

      if (this.validationErrors.length > 0) {
        this.$message.error('數據驗證未通過，請修正後重新上傳')
        return
      }

      this.importLoading = true

      // 準備要提交的數據 - 移除驗證用的臨時字段
      const submitData = this.parsedData.map((item) => {
        const { _valid, _errors, ...rest } = item
        return rest
      })

      // 調用批量新增API
      this.axios
        .post(`${this.baseURL}${this.basePort}/questionBank/batch-create`, { questions: submitData })
        .then((res) => {
          if (res.data.message === 'success') {
            this.$message.success(`成功導入 ${res.data.data.successCount || submitData.length} 條問答題目`)
            this.fetchQuestionBank() // 重新獲取列表
            this.closeImportModal()
          } else {
            this.$message.error(res.data.errorMsg || '批量導入問答題目失敗')
          }
        })
        .catch((error) => {
          console.error('批量導入問答題目出錯:', error)
          if (error.response && error.response.data) {
            this.$message.error(error.response.data.errorMsg || '批量導入問答題目失敗')
          } else {
            this.$message.error('批量導入問答題目失敗，請檢查網絡連接')
          }
        })
        .finally(() => {
          this.importLoading = false
        })
    },
    // 切換群組的展開/收起狀態
    toggleGroupExpansion (groupId) {
      // 使用 Vue.set 確保響應式更新
      this.$set(this.expandedGroups, groupId, !this.expandedGroups[groupId])
    },

    // 根據展開狀態獲取應顯示的機台
    getVisibleStores (record) {
      if (this.expandedGroups[record.id] || record.stores_data.length <= this.maxVisibleMachines) {
        return record.stores_data
      } else {
        return record.stores_data.slice(0, this.maxVisibleMachines)
      }
    },
    // ===== 機台群組批次匯入相關 =====

    // 顯示群組匯入模態窗
    showGroupImportModal () {
      this.groupImportModalVisible = true
      this.groupFileList = []
      this.groupParsedData = []
      this.groupValidationErrors = []
    },

    // 關閉群組匯入模態窗
    closeGroupImportModal () {
      this.groupImportModalVisible = false
    },

    // 下載機台群組模板（改成用店名）
    downloadGroupTemplate () {
      const templateData = [
        {
          群組名稱: '新北區',
          群組描述: '所有新北市的機台',
          機台名稱: '三峽民族' // 使用者填店名關鍵字
        },
        {
          群組名稱: '新北區',
          群組描述: '所有新北市的機台',
          機台名稱: '土城金安'
        },
        {
          群組名稱: '苗栗區',
          群組描述: '苗栗縣所有機台',
          機台名稱: '三義中正'
        }
      ]

      const ws = XLSX.utils.json_to_sheet(templateData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '機台群組模板')

      ws['!cols'] = [
        { wch: 20 },
        { wch: 30 },
        { wch: 30 } // 機台名稱欄位加寬
      ]

      // 使用說明也改一下
      const instructionData = [
        ['機台群組批次匯入說明'],
        [''],
        ['1. 每行代表一台機台加入哪個群組'],
        ['2. 同一個群組名稱的多行會自動合併成一個群組'],
        ['3. 群組名稱：必填，不可超過50字元'],
        ['4. 群組描述：可選'],
        ['5. 機台名稱：必填，填寫店名關鍵字（如「三義中正」、「土城金安」），系統會自動模糊匹配'],
        ['6. 若完全匹配不到或匹配到多台會標記錯誤，請填更精確的名稱'],
        ['7. 建議一次匯入不超過200台機台']
      ]
      const wsInst = XLSX.utils.aoa_to_sheet(instructionData)
      XLSX.utils.book_append_sheet(wb, wsInst, '使用說明')

      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' })
      FileSaver.saveAs(blob, '機台群組導入模板.xlsx')
    },

    // 群組上傳前處理
    beforeGroupUpload (file) {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'application/vnd.ms-excel'
      if (!isExcel) {
        this.$message.error('只能上傳Excel文件！')
        return false
      }
      if (file.size / 1024 / 1024 > 2) {
        this.$message.error('文件大小不能超過2MB！')
        return false
      }

      this.groupFileList = [file]
      this.groupParsedData = []
      this.groupValidationErrors = []
      this.parseGroupExcelFile(file)

      return false
    },

    // 移除群組檔案
    handleGroupRemove () {
      this.groupFileList = []
      this.groupParsedData = []
      this.groupValidationErrors = []
    },

    // 解析群組Excel
    parseGroupExcelFile (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]
          const jsonData = XLSX.utils.sheet_to_json(worksheet)

          if (jsonData.length === 0) {
            this.groupValidationErrors = ['文件中沒有找到任何數據']
            return
          }

          this.processGroupExcelData(jsonData)
        } catch (error) {
          this.groupValidationErrors = ['解析Excel文件失敗: ' + error.message]
        }
      }
      reader.readAsArrayBuffer(file)
    },

    // 處理群組Excel數據（核心邏輯）
    // 處理群組Excel數據（改用店名模糊匹配）
    processGroupExcelData (jsonData) {
      const rowMap = new Map() // key: 群組名稱, value: { name, description, macs: Set, displayNames: [] }
      const globalErrors = []

      // 取得所有機台列表（從 Vuex）
      const allDevices = this.TESTdeviceList || []

      // 檢查必要欄位（改成機台名稱）
      const firstRow = jsonData[0]
      const requiredCols = ['群組名稱', '機台名稱']
      const missing = requiredCols.filter((col) => !(col in firstRow))
      if (missing.length > 0) {
        globalErrors.push('缺少必要欄位: ' + missing.join(', '))
        this.groupValidationErrors = globalErrors
        return
      }

      jsonData.forEach((row, idx) => {
        const rowNum = idx + 2
        const groupName = (row['群組名稱'] || '').toString().trim()
        const description = (row['群組描述'] || '').toString().trim()
        const storeNameKeyword = (row['機台名稱'] || '').toString().trim()

        if (!groupName) {
          globalErrors.push(`第 ${rowNum} 行: 群組名稱不能為空`)
          return
        }
        if (groupName.length > 50) {
          globalErrors.push(`第 ${rowNum} 行: 群組名稱不能超過50字元`)
          return
        }
        if (!storeNameKeyword) {
          globalErrors.push(`第 ${rowNum} 行: 機台名稱不能為空`)
          return
        }

        // 模糊搜尋：device_name 包含使用者填的關鍵字
        const matchedDevices = allDevices.filter((device) => device.device_name && device.device_name.includes(storeNameKeyword))

        let deviceMac = null // 改成 camelCase
        let matchError = null

        if (matchedDevices.length === 0) {
          matchError = `找不到包含「${storeNameKeyword}」的機台`
        } else if (matchedDevices.length > 1) {
          const names = matchedDevices
            .slice(0, 3)
            .map((d) => d.device_name)
            .join('、')
          matchError = `找到多筆機台包含「${storeNameKeyword}」：${names} 等，請填更精確名稱`
        } else {
          // 剛好一筆 → 成功
          deviceMac = matchedDevices[0].device_mac
        }

        if (matchError) {
          globalErrors.push(`第 ${rowNum} 行: ${matchError}`)
          return
        }

        // 正確匹配到一筆，加入群組
        const key = groupName
        if (!rowMap.has(key)) {
          rowMap.set(key, {
            name: groupName,
            description: description,
            macs: new Set(),
            displayNames: [] // 用來預覽顯示使用者填的店名
          })
        }

        const groupItem = rowMap.get(key)
        if (groupItem.macs.has(deviceMac)) {
          globalErrors.push(`第 ${rowNum} 行: 群組 "${groupName}" 中重複加入相同機台（${storeNameKeyword}）`)
        } else {
          groupItem.macs.add(deviceMac)
          groupItem.displayNames.push(storeNameKeyword)
        }
      })

      // 轉換成預覽格式
      const parsedGroups = []
      // 建立以 device_mac 為鍵的 Map，將搜尋時間複雜度優化為 O(1)
      const deviceMap = new Map(allDevices.map((d) => [d.device_mac, d]))

      for (const group of rowMap.values()) {
        // 從 deviceMap 快速以 O(1) 複雜度取出完整機台資訊
        const storesData = Array.from(group.macs).map((mac) => {
          const fullDevice = deviceMap.get(mac)
          return {
            device_mac: mac,
            device_name: fullDevice?.device_name || '',
            city: fullDevice?.city || '',
            status: fullDevice?.status ?? 1 // 統一用數字 1（正常運行）
          }
        })

        const item = {
          name: group.name,
          description: group.description,
          stores_data: storesData, // 現在是完整資訊！
          machineCount: storesData.length,
          macList: Array.from(group.macs).join(', '),
          nameList: group.displayNames.join(', '), // 預覽用，顯示使用者填的關鍵字
          _valid: true,
          _errors: []
        }

        if (item.machineCount === 0) {
          item._valid = false
          item._errors.push('群組內無任何機台')
          globalErrors.push(`群組 "${group.name}": 沒有有效機台`)
        }

        parsedGroups.push(item)
      }

      this.groupParsedData = parsedGroups
      this.groupValidationErrors = globalErrors
    },

    // 提交群組批次匯入
    // 提交群組批次匯入（使用現有的單一 create API，一個一個新增）
    handleGroupImportSubmit () {
      if (this.groupParsedData.length === 0) {
        this.$message.warning('請先上傳並解析Excel文件')
        return
      }
      if (this.groupValidationErrors.length > 0) {
        this.$message.error('資料驗證未通過，請修正後再試')
        return
      }

      this.groupImportLoading = true

      // 準備要提交的群組資料（只保留必要欄位）
      const groupsToCreate = this.groupParsedData.map((g) => ({
        name: g.name.trim(),
        description: g.description ? g.description.trim() : '',
        stores_data: g.stores_data // 陣列內每個物件只含 device_mac
      }))

      // 依序一個一個呼叫現有的 create API
      const createPromises = groupsToCreate.map((group, index) => {
        return this.axios
          .post(`${this.baseURL}${this.basePort}/ads/group/create`, group)
          .then((res) => {
            if (res.data.message === 'success') {
              return { success: true, name: group.name }
            } else {
              return { success: false, name: group.name, error: res.data.errorMsg || '新增失敗' }
            }
          })
          .catch((err) => {
            console.error(`新增群組 "${group.name}" 失敗:`, err)
            let errorMsg = '未知錯誤'
            if (err.response) {
              if (err.response.status === 409) {
                errorMsg = '群組名稱已存在'
              } else {
                errorMsg = err.response.data.errorMsg || '新增失敗'
              }
            } else {
              errorMsg = '網路錯誤'
            }
            return { success: false, name: group.name, error: errorMsg }
          })
      })

      // 全部執行完後統計結果
      Promise.all(createPromises)
        .then((results) => {
          const successItems = results.filter((r) => r.success)
          const failedItems = results.filter((r) => !r.success)

          if (failedItems.length === 0) {
            this.$message.success(`成功匯入 ${successItems.length} 個機台群組`)
          } else {
            // 有部分失敗時，顯示詳細訊息
            const failedNames = failedItems.map((f) => `${f.name}（${f.error}）`).join('、')
            this.$message.warning(`部分匯入成功：${successItems.length} 個成功，${failedItems.length} 個失敗（${failedNames}）`)
          }

          // 無論成功失敗，都重新載入列表並關閉 modal
          this.fetchMachineGroups()
          this.closeGroupImportModal()
        })
        .catch(() => {
          this.$message.error('匯入過程中發生錯誤')
        })
        .finally(() => {
          this.groupImportLoading = false
        })
    }
  }
}
</script>

<style scoped>
/* 自定義樣式 */
.ant-form-item-label {
  font-weight: 600;
}

.ant-form-item {
  margin-bottom: 6px;
}

:deep(.ant-table-thead > tr > th) {
  background-color: #f0f5ff;
  font-weight: 600;
}

:deep(.ant-tabs-card > .ant-tabs-bar .ant-tabs-tab) {
  border-radius: 8px 8px 0 0;
  font-weight: 600;
}

:deep(.ant-tabs-card > .ant-tabs-bar .ant-tabs-tab-active) {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  color: white;
}

.ant-modal {
  top: 10px !important;
}

.custom-modal-position .ant-modal {
  top: 10px !important;
}
</style>
